import { atom } from 'jotai';
import { useReducerAtom } from 'jotai/utils';
import JSB from '@bridge/sharee';

export enum ReportProgress {
  EMAIL_INPUT,
  EMAIL_VERIFY,
  REPORT_FORM,
  RESULT,
}

interface EmailVerifyContextState {
  email?: string;
  lastSendCodeTime: number;
  toast?: {
    content: string;
    time: number;
  };
  report_url?: string;
  report_type?: string;
  progress: ReportProgress;
}

const defaultEmailVerifyContextState: EmailVerifyContextState = {
  lastSendCodeTime: -1,
  progress: ReportProgress.EMAIL_INPUT,
};

export enum EmailVerifyContextReducerKey {
  UPDATE_SEND_TIME = 'update-send-time',
  UPDATE_EMAIL = 'update-email',
  UPDATE_QUERY = 'update-query',
  UPDATE_PROGRESS = 'update-progress',
}

type EmailVerifyContextReducer =
  | {
      type: EmailVerifyContextReducerKey.UPDATE_SEND_TIME;
      payload: {
        isInApp: boolean;
        time?: number;
      };
    }
  | {
      type: EmailVerifyContextReducerKey.UPDATE_EMAIL;
      payload: {
        email: string;
      };
    }
  | {
      type: EmailVerifyContextReducerKey.UPDATE_QUERY;
      payload: {
        report_type: string;
        report_url: string;
      };
    }
  | {
      type: EmailVerifyContextReducerKey.UPDATE_PROGRESS;
      payload: {
        progress: ReportProgress;
      };
    };

export const REPORT_LAST_EMAIL_SEND = 'REPORT_LAST_EMAIL_SEND';

const emailVerifyReducer = (state: EmailVerifyContextState, action: EmailVerifyContextReducer) => {
  switch (action.type) {
    case EmailVerifyContextReducerKey.UPDATE_SEND_TIME: {
      const { isInApp, time } = action.payload;
      const now = time || Date.now();

      if (isInApp) {
        void JSB.app.setValueForKey({
          for_key: REPORT_LAST_EMAIL_SEND,
          value: now,
        });
      } else {
        localStorage.setItem(REPORT_LAST_EMAIL_SEND, now.toString());
      }

      return {
        ...state,
        lastSendCodeTime: now,
      };
    }
    case EmailVerifyContextReducerKey.UPDATE_EMAIL: {
      const { email } = action.payload;
      return {
        ...state,
        email,
      };
    }
    case EmailVerifyContextReducerKey.UPDATE_QUERY: {
      const { report_type, report_url } = action.payload;
      return {
        ...state,
        report_url,
        report_type,
      };
    }
    case EmailVerifyContextReducerKey.UPDATE_PROGRESS: {
      const { progress } = action.payload;
      return {
        ...state,
        progress,
      };
    }
    default:
      throw new Error();
  }
};

const emailVerifyAtom = atom(defaultEmailVerifyContextState);

export const useEmailVerifyReducer = () => {
  return useReducerAtom(emailVerifyAtom, emailVerifyReducer);
};
